import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useClaimAudit } from '@/hooks/useClaimAudit';
import type { VerificationReview } from '@/hooks/useVerificationAdmin';
import type { VerificationStatus } from '@/lib/businessVerify';
import {
  ShieldCheck,
  ShieldX,
  Loader2,
  ExternalLink,
  CircleCheck,
  CircleAlert,
  CircleHelp,
  Copy,
  Check,
} from 'lucide-react';

interface ClaimReviewCardProps {
  review: VerificationReview;
  onIssue: (businessPubkey: string, txid?: string) => Promise<void>;
  onRevoke: (businessPubkey: string) => Promise<void>;
  isSubmitting: boolean;
}

const STATUS_META: Record<VerificationStatus, { label: string; className: string }> = {
  PENDING: { label: 'Pending review', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  VERIFIED: { label: 'Verified', className: 'bg-bch/10 text-bch' },
  EXPIRED: { label: 'Expired', className: 'bg-muted text-muted-foreground' },
  UNVERIFIED: { label: 'Unverified', className: 'bg-muted text-muted-foreground' },
};

function AuditBadge({ txid, expectedSats }: { txid?: string; expectedSats: number }) {
  const { data, isLoading } = useClaimAudit(txid, expectedSats);

  if (!txid) {
    return (
      <Badge variant="outline" className="gap-1 text-xs">
        <CircleHelp className="size-3" /> No txid
      </Badge>
    );
  }
  if (isLoading) {
    return (
      <Badge variant="outline" className="gap-1 text-xs">
        <Loader2 className="size-3 animate-spin" /> Checking…
      </Badge>
    );
  }
  if (!data) return null;

  switch (data.verdict) {
    case 'paid':
      return (
        <Badge className="gap-1 text-xs bg-bch/10 text-bch">
          <CircleCheck className="size-3" /> Paid {(data.paidSats! / 1e8).toFixed(6)} BCH
        </Badge>
      );
    case 'underpaid':
      return (
        <Badge className="gap-1 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <CircleAlert className="size-3" /> Underpaid
        </Badge>
      );
    case 'wrong-recipient':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <CircleAlert className="size-3" /> Wrong recipient
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="gap-1 text-xs">
          <CircleHelp className="size-3" /> Not found
        </Badge>
      );
  }
}

export function ClaimReviewCard({ review, onIssue, onRevoke, isSubmitting }: ClaimReviewCardProps) {
  const { businessPubkey, claim, grant, state } = review;
  const author = useAuthor(businessPubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? meta?.display_name ?? 'Unknown business';
  const npub = nip19.npubEncode(businessPubkey);
  const [copied, setCopied] = useState(false);

  const statusMeta = STATUS_META[state.status];
  const expectedSats = claim?.amountSats ?? 0;

  const copyTxid = async () => {
    if (!claim?.txid) return;
    try {
      await navigator.clipboard.writeText(claim.txid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const isVerified = state.status === 'VERIFIED';

  return (
    <Card>
      <CardContent className="py-4 px-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <Avatar className="size-10">
            <AvatarImage src={meta?.picture} alt={name} />
            <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">{name}</span>
              <Badge className={`text-[10px] py-0 px-1.5 ${statusMeta.className}`}>
                {statusMeta.label}
              </Badge>
            </div>
            <a
              href={`https://bchnostr.com/profile/${npub}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground font-mono hover:text-foreground inline-flex items-center gap-1"
            >
              {npub.slice(0, 14)}…{npub.slice(-6)}
              <ExternalLink className="size-3" />
            </a>
          </div>
        </div>

        {/* Payment info */}
        <div className="rounded-lg bg-muted/40 px-3 py-2 space-y-1.5 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">On-chain payment</span>
            <AuditBadge txid={claim?.txid} expectedSats={expectedSats} />
          </div>
          {claim?.txid ? (
            <div className="flex items-center gap-1.5">
              <span className="font-mono truncate flex-1">{claim.txid.slice(0, 20)}…</span>
              <button onClick={copyTxid} className="text-muted-foreground hover:text-foreground" aria-label="Copy txid">
                {copied ? <Check className="size-3 text-bch" /> : <Copy className="size-3" />}
              </button>
              <a
                href={`https://bchexplorer.cash/tx/${claim.txid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                aria-label="View on explorer"
              >
                <ExternalLink className="size-3" />
              </a>
            </div>
          ) : (
            <div className="text-muted-foreground">No payment claim submitted</div>
          )}
          {isVerified && grant && (
            <div className="text-muted-foreground pt-1">
              Grant valid until {new Date(grant.expiresAt * 1000).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 bg-bch hover:bg-bch/90 text-bch-foreground"
            disabled={isSubmitting || isVerified}
            onClick={() => onIssue(businessPubkey, claim?.txid)}
          >
            {isSubmitting ? <Loader2 className="size-4 mr-1.5 animate-spin" /> : <ShieldCheck className="size-4 mr-1.5" />}
            {isVerified ? 'Already granted' : state.status === 'EXPIRED' ? 'Renew grant' : 'Issue grant'}
          </Button>
          {isVerified && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={isSubmitting}
              onClick={() => onRevoke(businessPubkey)}
            >
              <ShieldX className="size-4 mr-1.5" />
              Revoke
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
